import { useCallback, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { cn } from "../../../../lib/utils";
import { useClaimMotion } from "./claimMotion";

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * The dialog shell used by both of this page's modals.
 *
 * The originals were `fixed inset-0` divs toggled with a `hidden` class. That
 * meant: no Escape key, no focus trap, focus left behind on the page underneath,
 * the background still scrolling behind the overlay, and — because they only had
 * their `hidden` class removed — no way to animate the exit.
 *
 * Portaled to `document.body`: this page renders inside the layout's
 * framer-motion page transition, and an ancestor with a transform becomes the
 * containing block for `position: fixed`, which would anchor the overlay to the
 * page content instead of the viewport. Other flows in this codebase portal for
 * the same reason.
 *
 * A sheet on phones and a centred dialog from 640px up, which is where a
 * full-width panel starts to look like a mistake rather than a choice.
 */
const ClaimDialog = ({
  open,
  onClose,
  title,
  description,
  children,
  contentClassName,
}) => {
  const translate = useLanguageTranslate();
  const motionPresets = useClaimMotion();
  const panelRef = useRef(null);
  const previouslyFocused = useRef(null);
  const headingId = useId();
  const descriptionId = useId();

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose?.();
        return;
      }
      if (event.key !== "Tab") return;

      // Minimal focus trap: cycle within the panel so Tab cannot reach the page
      // behind the overlay.
      const focusables = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!focusables?.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus the panel itself rather than its first control: landing on "Close"
    // reads the wrong thing first, and landing on an input opens the keyboard
    // on a phone before the customer has read anything.
    const focusTimer = window.setTimeout(() => panelRef.current?.focus(), 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      // Return focus to whatever opened the dialog.
      if (previouslyFocused.current instanceof HTMLElement) {
        previouslyFocused.current.focus();
      }
    };
  }, [open]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[999] flex items-end justify-center sm:items-center sm:p-4">
          <motion.div
            {...motionPresets.overlay}
            onClick={onClose}
            className="absolute inset-0 bg-[#1A2024]/50"
            aria-hidden="true"
          />

          <motion.div
            {...motionPresets.dialog}
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? headingId : undefined}
            aria-describedby={description ? descriptionId : undefined}
            tabIndex={-1}
            onKeyDown={onKeyDown}
            className={cn(
              "relative z-10 flex max-h-[92vh] w-full flex-col overflow-hidden bg-white shadow-[0_20px_48px_rgba(26,32,36,0.24)] focus:outline-none",
              "rounded-t-2xl sm:max-h-[88vh] sm:rounded-2xl",
              contentClassName || "sm:max-w-md"
            )}
          >
            <div className="flex items-start gap-4 px-5 pb-3 pt-5 sm:px-6 sm:pt-6">
              <div className="min-w-0 flex-1">
                {title && (
                  <h2
                    id={headingId}
                    className="text-lg font-semibold leading-7 tracking-[-0.18px] text-[#1A2024]"
                  >
                    {title}
                  </h2>
                )}
                {description && (
                  <p
                    id={descriptionId}
                    className="mt-1 text-sm leading-5 text-[#6E7C87]"
                  >
                    {description}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={onClose}
                aria-label={translate("Close", "إغلاق")}
                className="-me-1 -mt-1 flex size-9 shrink-0 items-center justify-center rounded-full text-[#5B6871] transition-colors hover:bg-[#F0F3F5] hover:text-[#1A2024] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A9E7]"
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-6 sm:px-6 [-webkit-overflow-scrolling:touch]">
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ClaimDialog;
